import { lazy, Suspense } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Trans, useTranslation } from 'react-i18next';
import { Badge, Card, Eyebrow, Kbd, PageHeader } from '@mkcore/ui';
import { useCan, useMe } from '@/lib/session';
import { modKeyLabel } from '@/lib/shortcuts';
import { navItems } from '@/components/nav';

// Recharts loads only for people who see the dashboard (docs/01-architectuur/11-frontend.md).
const HoursDashboard = lazy(() => import('@/features/reporting/dashboard').then((m) => ({ default: m.HoursDashboard })));

export const Route = createFileRoute('/_app/')({ component: Home });

function Home() {
  const { t } = useTranslation();
  const me = useMe();
  const canReport = useCan('reporting:read');
  const links = navItems.filter((n) => n.to !== '/' && (n.permission === 'authenticated' || me.permissions.includes(n.permission)));
  return (
    <>
      <PageHeader eyebrow={t('app.tagline')} title={t('home.greeting', { name: me.name })} description={t('home.intro')} />
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-5">
          <Eyebrow>{t('home.yourRoles')}</Eyebrow>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {me.roles.map((r) => (
              <Badge key={r} tone="outline">
                {r}
              </Badge>
            ))}
          </div>
          <p className="mt-4 text-sm text-fg-2">
            <Trans i18nKey="home.palette" values={{ keys: `${modKeyLabel()} K` }} components={{ kbd: <Kbd /> }} />
          </p>
        </Card>
        <Card className="p-5 md:col-span-2">
          <Eyebrow>{t('home.quickLinks')}</Eyebrow>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {links.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="flex items-center gap-2 rounded-md border border-line-subtle px-3 py-2.5 text-sm hover:border-gold [&_svg]:size-4 [&_svg]:text-fg-3"
                >
                  {l.icon}
                  {t(l.labelKey)}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      {canReport && (
        <Suspense fallback={null}>
          <HoursDashboard />
        </Suspense>
      )}
    </>
  );
}
